import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, PhoneCall, RefreshCw } from "lucide-react";

const colore = (p) => (p === 100 ? "bg-emerald-500" : p >= 50 ? "bg-amber-500" : "bg-rose-500");

const Barra = ({ percent }) => (
  <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
    <div className={`h-full rounded-full transition-all ${colore(percent)}`} style={{ width: `${percent}%` }} />
  </div>
);

const quando = (iso) => {
  if (!iso) return "mai entrato";
  const g = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return g <= 0 ? "oggi" : g === 1 ? "ieri" : `${g} giorni fa`;
};

export const AvvioLocali = ({ client }) => {
  const [data, setData] = useState(null);
  const [aperto, setAperto] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await client.get("/admin/onboarding");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scheda di avvio non disponibile");
    }
  }, [client]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return null;
  const { rows, call_back: richiamare, totals } = data;

  return (
    <div className="space-y-4" data-testid="avvio-locali">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ["Clienti veri", totals.clients],
          ["Pronti a lavorare", totals.ready],
          ["In configurazione", totals.started],
          ["Mai entrati", totals.never],
        ].map(([k, v]) => (
          <Card key={k} className="border-slate-800 bg-[#111827] p-3 text-slate-100">
            <p className="text-[10px] uppercase tracking-widest text-slate-500">{k}</p>
            <p className="mt-1 text-2xl font-bold">{v}</p>
          </Card>
        ))}
      </div>

      <Card className="border-slate-800 bg-[#111827] p-4 text-slate-100" data-testid="avvio-richiamare">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-amber-300">
            <PhoneCall className="mr-1.5 inline h-4 w-4" /> Da richiamare ({richiamare.length})
          </p>
          <Button size="sm" variant="ghost" className="text-slate-400" onClick={load} data-testid="avvio-refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Chi non ha finito la configurazione ed è fermo da 3 giorni o più, o non è mai entrato.
        </p>
        <div className="mt-3 space-y-2">
          {richiamare.map((r) => (
            <div key={r.id} className="rounded-lg bg-slate-900 p-3" data-testid={`richiamare-${r.slug}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{r.name}</span>
                <span className="text-xs text-slate-500">{r.plan}</span>
                <span className="text-xs text-amber-300">{quando(r.last_access)}</span>
                <span className="ml-auto font-num text-sm font-bold">{r.percent}%</span>
              </div>
              <div className="mt-2">
                <Barra percent={r.percent} />
              </div>
              <p className="mt-2 text-xs text-slate-400">Gli manca: {r.missing.join(", ")}</p>
              {(r.phone || r.contact) && (
                <p className="mt-1 text-xs text-slate-500">
                  {r.phone && (
                    <a className="text-amber-300 hover:underline" href={`tel:${r.phone}`}>
                      {r.phone}
                    </a>
                  )}
                  {r.phone && r.contact ? " · " : ""}
                  {r.contact}
                </p>
              )}
            </div>
          ))}
          {!richiamare.length && <p className="text-sm text-slate-500">Nessuno da richiamare: sono tutti in pista.</p>}
        </div>
      </Card>

      <Card className="border-slate-800 bg-[#111827] p-4 text-slate-100" data-testid="avvio-elenco">
        <p className="text-sm font-semibold">Avanzamento di ogni locale</p>
        {!rows.length && (
          <p className="text-sm text-slate-500" data-testid="avvio-vuoto">
            Nessun cliente vero al momento: demo, vetrine e prove interne restano fuori da questo elenco.
          </p>
        )}
        <div className="mt-3 divide-y divide-slate-800">
          {rows.map((r) => (
            <div key={r.id} className="py-3" data-testid={`avvio-${r.slug}`}>
              <button
                className="flex w-full flex-wrap items-center gap-2 text-left"
                onClick={() => setAperto(aperto === r.id ? null : r.id)}
              >
                <span className="font-semibold">{r.name}</span>
                {r.demo && <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-400">demo</span>}
                <span className="text-xs text-slate-500">{r.plan}</span>
                <span className="text-xs text-slate-500">· ultimo accesso {quando(r.last_access)}</span>
                <span className="font-num ml-auto text-sm font-bold">{r.percent}%</span>
              </button>
              <p className="mt-1 text-[11px] text-slate-400" data-testid={`avvio-pin-sintesi-${r.slug}`}>
                {r.counts.persone === 0
                  ? "Nessuna persona attiva"
                  : <>
                      {r.counts.entrati} su {r.counts.persone} hanno già usato il PIN
                      {r.counts.pin_partenza > 0 && (
                        <span className="text-amber-300"> · {r.counts.pin_partenza} con il PIN di partenza</span>
                      )}
                    </>}
              </p>
              <div className="mt-2">
                <Barra percent={r.percent} />
              </div>
              {aperto === r.id && (
                <div className="mt-3 grid gap-1 sm:grid-cols-2" data-testid={`avvio-passi-${r.slug}`}>
                  {r.steps.map((s) => (
                    <p key={s.id} className={`flex items-center gap-1.5 text-xs ${s.done ? "text-emerald-300" : "text-slate-500"}`}>
                      {s.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                      {s.label}
                    </p>
                  ))}
                  <p className="text-xs text-slate-500 sm:col-span-2">
                    {r.counts.sale} sale · {r.counts.tavoli} tavoli · {r.counts.piatti} piatti · {r.counts.persone} persone
                  </p>
                  <div className="sm:col-span-2" data-testid={`avvio-pin-${r.slug}`}>
                    <p className="text-xs text-slate-400">
                      PIN: <span className="font-semibold text-emerald-300">{r.counts.pin_personali} personali</span>
                      {r.counts.pin_partenza > 0 && (
                        <span className="text-amber-300"> · {r.counts.pin_partenza} ancora con il PIN di partenza</span>
                      )}
                      {" · "}{r.counts.entrati}/{r.counts.persone} sono entrati almeno una volta
                    </p>
                    {r.counts.esempi > 0 && (
                      <p className="text-xs font-semibold text-amber-300" data-testid={`avvio-esempi-${r.slug}`}>
                        {r.counts.esempi} persone di esempio da sostituire (mai entrate)
                      </p>
                    )}
                    <div className="mt-1.5 space-y-0.5">
                      {(r.team || []).map((m) => (
                        <p key={m.name} className="flex flex-wrap items-center gap-1.5 text-[11px]">
                          <span className="font-semibold text-slate-200">{m.name}</span>
                          <span className="text-slate-500">{m.role}</span>
                          <span className={m.pin_personale ? "text-emerald-300" : "text-amber-300"}>
                            {m.pin_personale ? "PIN personale" : "PIN di partenza"}
                          </span>
                          <span className="text-slate-500">· {m.last_login ? `PIN digitato ${quando(m.last_login)}` : "PIN mai digitato"}</span>
                        </p>
                      ))}
                      {!(r.team || []).length && <p className="text-[11px] text-slate-500">Nessuna persona attiva.</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
